import { useEffect, useMemo, useRef, useState, type RefObject } from 'react';
import { createPortal } from 'react-dom';
import { anchorTo, Icon, TextInput, useAnchorRect } from '@forecastify/ui';
import { formatHours } from '@forecastify/shared';
import { useI18n, useT } from '../../i18n';

/**
 * ┌───────────────────────────────────────────────────────────────────────────┐
 * │ C53 · which deliverable to plan somebody onto.                            │
 * └───────────────────────────────────────────────────────────────────────────┘
 *
 * ⚠️⚠️ **A MENU IS THE WRONG CONTROL ONCE THE LIST IS A WORKSPACE'S DELIVERABLES.** This was a
 * plain dropdown of every open line item: fine at the fixture's dozen, and a scroll through
 * several hundred in any workspace that has been running a year — with no way to narrow it, and no
 * way to tell two deliverables of the same name on different contracts apart.
 *
 * ⚠️ **So it searches, and every row says where it lives and what it is.** Project, contract,
 * estimate and window — the mockup's own four — which between them answer *is this the one* for
 * the two rows that would otherwise read identically.
 */

export interface PickableLineItem {
  readonly id: string;
  readonly name: string;
  readonly projectName: string | null;
  /** The contract's public identifier, as every screen names a contract (R-57). */
  readonly contractLabel: string | null;
  readonly estimateHours: string;
  readonly startsOn: string | null;
  readonly endsOn: string | null;
}

/** One row of a picker: what it is, and the caption that tells it apart from its namesakes. */
interface PickerRow {
  readonly id: string;
  readonly title: string;
  readonly caption: string;
}

/**
 * ⚠️ **The surface both pickers share** — the search, the capped list, the dismissal rules. What
 * differs is what a row says and what an empty list means, and both are the caller's.
 */
function PickerSurface({
  anchor,
  label,
  search,
  none,
  rows,
  onPick,
  onClose,
}: {
  readonly anchor: RefObject<HTMLElement | null>;
  readonly label: string;
  readonly search: string;
  readonly none: string;
  readonly rows: readonly PickerRow[];
  readonly onPick: (id: string) => void;
  readonly onClose: () => void;
}) {
  const t = useT();
  const { rect } = useAnchorRect(true, anchor);
  const surface = useRef<HTMLDivElement | null>(null);
  const [query, setQuery] = useState('');

  useEffect(() => {
    const onKey = (event: KeyboardEvent): void => {
      if (event.key === 'Escape') onClose();
    };
    const onDown = (event: MouseEvent): void => {
      const node = event.target;
      if (!(node instanceof Node)) return;
      if (surface.current?.contains(node) === true) return;
      if (anchor.current?.contains(node) === true) return;
      /**
       * ⚠️⚠️ **A MENU OPENED FROM IN HERE IS NOT *OUTSIDE*, THOUGH THE DOM SAYS IT IS.**
       * `DropdownMenu` portals its surface into `document.body`, so it is nowhere near this one in
       * the tree — and `mousedown` runs BEFORE `click`. Without this, choosing an overtime type
       * closed the pop-over on the way down and the item's own handler never ran: every menu in
       * here silently did nothing. Found by a test, not by looking, because the pop-over shutting
       * is exactly what a reader expects to see after choosing something.
       */
      if (node instanceof Element && node.closest('[role="menu"]') !== null) return;
      onClose();
    };
    document.addEventListener('keydown', onKey);
    document.addEventListener('mousedown', onDown);
    return () => {
      document.removeEventListener('keydown', onKey);
      document.removeEventListener('mousedown', onDown);
    };
  }, [anchor, onClose]);

  /**
   * ⚠️ **The caption is searched as well as the title**, because that is how somebody who knows
   * the commercial side looks for a deliverable — *cont_12a3cd5a* rather than *Havi támogatás* —
   * and it is the field the mockup's own placeholder names second.
   */
  const shown = useMemo(() => {
    const needle = query.trim().toLowerCase();
    if (needle === '') return rows;
    return rows.filter((row) => `${row.title} ${row.caption}`.toLowerCase().includes(needle));
  }, [rows, query]);

  if (typeof document === 'undefined') return null;

  return createPortal(
    <div
      ref={surface}
      role="dialog"
      aria-label={label}
      className="bg-popover text-popover-foreground shadow-pop border-border w-[340px] overflow-hidden rounded-lg border"
      style={anchorTo(rect, { side: 'bottom', align: 'start', width: 340 })}
    >
      <div className="border-b p-2">
        <TextInput
          autoFocus
          value={query}
          placeholder={search}
          aria-label={search}
          className="h-8 text-[12px]"
          onChange={(event) => {
            setQuery(event.target.value);
          }}
        />
      </div>

      {/* ⚠️ Capped height and scrolled: a workspace's deliverables are not a list that fits. */}
      <div className="max-h-[280px] overflow-y-auto p-1">
        {shown.length === 0 ? (
          <p className="text-muted-foreground px-2 py-6 text-center text-[11.5px]">
            {query.trim() === '' ? none : t('resource.pick.noMatch')}
          </p>
        ) : (
          shown.map((row) => (
            <button
              key={row.id}
              type="button"
              className="hover:bg-accent flex w-full flex-col items-start gap-0.5 rounded-md px-2 py-1.5 text-left transition-colors"
              onClick={() => {
                onPick(row.id);
              }}
            >
              <span className="w-full truncate text-[12.5px] font-medium">{row.title}</span>
              {row.caption === '' ? null : (
                <span className="text-muted-foreground w-full truncate text-[10.5px]">
                  {row.caption}
                </span>
              )}
            </button>
          ))
        )}
      </div>
    </div>,
    document.body,
  );
}

export function LineItemPicker({
  anchor,
  items,
  onPick,
  onClose,
}: {
  readonly anchor: RefObject<HTMLElement | null>;
  readonly items: readonly PickableLineItem[];
  readonly onPick: (item: PickableLineItem) => void;
  readonly onClose: () => void;
}) {
  const t = useT();
  const { locale } = useI18n();

  const day = useMemo(
    () => new Intl.DateTimeFormat(locale, { month: 'short', day: 'numeric' }),
    [locale],
  );

  const window = (item: PickableLineItem): string => {
    if (item.startsOn === null && item.endsOn === null) return '';
    const from = item.startsOn === null ? '' : day.format(new Date(`${item.startsOn}T00:00:00Z`));
    const to = item.endsOn === null ? '' : day.format(new Date(`${item.endsOn}T00:00:00Z`));
    return `${from} – ${to}`;
  };

  return (
    <PickerSurface
      anchor={anchor}
      label={t('resource.item.add')}
      search={t('resource.pick.search')}
      none={t('resource.pick.none')}
      rows={items.map((item) => ({
        id: item.id,
        title: item.name,
        caption: [
          item.projectName,
          item.contractLabel,
          t('resource.pick.estimate', { hours: formatHours(Number(item.estimateHours)) }),
          window(item),
        ]
          .filter((part) => part !== null && part !== '')
          .join(' · '),
      }))}
      onPick={(id) => {
        const item = items.find((one) => one.id === id);
        if (item !== undefined) onPick(item);
      }}
      onClose={onClose}
    />
  );
}

/** Somebody a line item could be given to — a member of its project. */
export interface PickablePerson {
  readonly id: string;
  readonly name: string;
  readonly roleName: string | null;
}

/**
 * ⚠️⚠️ **U-15 · THE OTHER DIRECTION: WHO TO PUT ON THIS DELIVERABLE.** The project tab's line-item
 * grid adds a PERSON under a line item where the people grid adds a line item under a person. The
 * caller passes only the project's members (P-1) — the server refuses anybody else.
 */
export function PersonPicker({
  anchor,
  people,
  onPick,
  onClose,
}: {
  readonly anchor: RefObject<HTMLElement | null>;
  readonly people: readonly PickablePerson[];
  readonly onPick: (person: PickablePerson) => void;
  readonly onClose: () => void;
}) {
  const t = useT();
  return (
    <PickerSurface
      anchor={anchor}
      label={t('resource.person.add')}
      search={t('resource.pickPerson.search')}
      none={t('resource.pickPerson.none')}
      rows={people.map((person) => ({
        id: person.id,
        title: person.name,
        caption: person.roleName ?? '',
      }))}
      onPick={(id) => {
        const person = people.find((one) => one.id === id);
        if (person !== undefined) onPick(person);
      }}
      onClose={onClose}
    />
  );
}

/** The trigger, so the grid does not have to know how the picker is opened. */
export function PickerTrigger({
  label,
  onOpen,
}: {
  readonly label: string;
  readonly onOpen: (element: HTMLElement) => void;
}) {
  return (
    <button
      type="button"
      className="text-muted-foreground hover:bg-accent hover:text-foreground flex items-center gap-1.5 rounded-md px-2 py-1 text-[11.5px] transition-colors"
      onClick={(event) => {
        onOpen(event.currentTarget);
      }}
    >
      <Icon name="plus" size={13} />
      {label}
    </button>
  );
}
